import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { 
    PageHeader, 
    PageSection, 
    SecondaryCallout,
    RelatedContent, 
    CrossLink, 
    IndicationPageIntro, 
    AnchorLinks,
    PrimaryClaim,
    KMCurve,
    FeaturedStat,
    ComparisonTable,
    PageSubSection,
    PatientProfile,
    ReferencesBlock,
    TextBlock,
    NCCNcallout,
    AccordionGroup,
    ForestPlot
} from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import { jobCodes } from '../../util/data/jobCodes';
import NSCLC_NEOADJUVANT from '../../components/StudyDesign/__study-design/nsclc-perioperative-regimen';
import { DefinitionBlock, Heading, NotationBlock, WYSIWYG } from '../../util/typography';
import styled from 'styled-components';
import { createMarkup } from '../../util/functions';
import AccordionDrawer from '../../components/layout/AccordionGroup/AccordionDrawer';

// SEO INFORMATION 
const pageMeta = {
    title: `KEYNOTE-671 - Clinical Trial Results & Study Design`,
    keywords: `keynote-671`,
    description: `Health care professionals may review clinical findings from KEYNOTE-671 for neoadjuvant combination therapy followed by adjuvant monotherapy after surgery in patients with resectable non–small cell lung cancer (NSCLC).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/nsclc-perioperative-regimen/","@type":"ApprovedIndication","name":"KEYTRUDA® and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated for the treatment of adult patients with resectable (tumors ≥4 cm or node positive) non–small cell lung cancer (NSCLC) in combination with platinum-containing chemotherapy as neoadjuvant treatment, and then continued as a single agent as adjuvant treatment after surgery."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/nsclc-perioperative-regimen/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/nsclc-perioperative-regimen/","@type":"MedicalWebPage","name":"Neoadjuvant Combination Therapy and Then Continued as Adjuvant Monotherapy After Surgery, for Resectable (Tumors ≥4 cm or Node Positive) NSCLC","description":"KEYTRUDA Is the Only Anti–PD-1, as Part of a Neoadjuvant Followed by Adjuvant Regimen, to Demonstrate Statistically Significant Improvement in Overall Survival in Resectable (T ≥4 cm or N+) NSCLC vs Placebo + Chemo/Placebo"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/nsclc-perioperative-regimen/","@type":"MedicalTrial","name":"KEYNOTE-671","description":"KEYNOTE⁠-⁠671: A randomized, double-blind, multicenter, placebo-controlled, phase 3 study in patients with resectable stage II, IIIA, or IIIB (N2) NSCLC per AJCC 8th edition","trialDesign":"RandomizedTrial"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/nsclc-perioperative-regimen/","@type":"MedicalGuideline","guideline":"NCCN CATEGORY 1 RECOMMENDATION Pembrolizumab (KEYTRUDA) as Part of a Neoadjuvant Regimen and Then Continued as Adjuvant Treatment After Surgery for Certain Patients With Resectable (T ≥4 cm or N+) NSCLC, Regardless of PD-L1 Expression","recognizingAuthority":"NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)"}`
    ]
}

const pathologicalResponseSubhead = `First Interim Analysis (Median Follow-Up Time: 25.2 Months)<sup>4</sup>`

const htmlPRR = `The trial demonstrated a statistically significant difference in pathological complete response (pCR) rate and major pathological response (mPR) rate:`;

const StudyTitle = styled(Heading.H4)`
    margin-bottom: 10px;
`;

const CustomSpacing40 = styled.div`
    margin-bottom: 40px;
    > div > div > div {
    // margin-bottom: 30px;
    } 
`;
const CustomSpacing30 = styled.div`
> div{
    margin-bottom: 20px !important;
}
`;
const PageStyle = styled.div`
section{
@media only screen and (max-width: 768px) {
padding: 80px 0px;
}
@media only screen and (min-width: 1024px) {
padding: 100px 0px;
}
}
.mbbottom10{
@media only screen and (max-width: 768px) {
margin-bottom: 10px;
}
@media only screen and (min-width: 1024px) {
margin-bottom: 0px;
}
}
`;

const CustomNCCNCalloutWrapper  = styled.div`
> div {
margin-bottom: 0px;
margin-top: 20px;
}
`

const CustomAccordianSpacing = styled.div`
 > div > div  > div > div > div {
    margin-bottom: 8px;
    @media only screen and (max-width: 768px) {
      margin-bottom: 8px;
      margin-top: 30px;
    }
  }
    > div {
    margin-bottom: 30px;
    }
     > div > div  > div > div > div > div > div > div:nth-of-type(2) {
    div{
     @media only screen and (max-width: 768px) {
        padding: 10px;
    }
    }
    }
`;

const CustomPageSection = styled.div`
@media only screen and (max-width: 768px) {
section{
    padding-bottom: 40px;
}
}
`;

const StyledTextBox = styled.div`
> div{
    margin: 10px auto 19px !important;
}
`;
const StyledKMCurve = styled.div`
> div{
    margin-top: 9px !important;
     margin-bottom: 30px;
}
    
`;

//from /src/util/data/indications.js
const indicationId = 34; 
const jobCode = jobCodes[4].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Additional Clinical Data for KEYTRUDA",
            items: [
                { text: '1L Combo Therapy – Nonsquamous ', url: '/efficacy/nsclc-nonsquamous-first-line-combination-therapy' },
                { text: '1L Combo Therapy – Squamous ', url: '/efficacy/nsclc-squamous-first-line-combination-therapy' },
                { text: '1L Monotherapy – Nonsquamous & Squamous', url: '/efficacy/nsclc-first-line-monotherapy' },
                { text: '2L Monotherapy – Nonsquamous & Squamous', url: '/efficacy/nsclc-second-line-monotherapy' },
                { text: 'Adjuvant Therapy for Stage IB (T2a ≥4 cm), Stage II, or Stage IIIA NSCLC', url: '/efficacy/nsclc-adjuvant-therapy' },
            ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/nsclc-perioperative-regimen' },
                { text: 'Dosing', url: '/dosing/options/#nsclc' },
                { text: 'PD&#8288;-&#8288;L1 Testing & Scoring', url: '/biomarker-testing/pd-l1/' },
            ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const featuredStatFootnotesOne = [
    {
      label: 'a.',
      text: 'Chemotherapy consisted of cisplatin 75 mg/m<sup>2</sup> IV on day 1 + (pemetrexed 500 mg/m<sup>2</sup> IV on day 1 or gemcitabine 1,000 mg/m<sup>2</sup> IV on days 1 and 8) of each 21-day cycle for up to 4 cycles.'
    }
  ]

const featuredStatFootnotesTwo = [
  {
    label: 'b.',
    text: 'HR based on Cox regression model with treatment as a covariate stratified by stage, tumor PD&#8288;-&#8288;L1 expression, histology, and geographic region.'
  },
  {
    label: 'c.',
    text: '<i>P</i> value based on stratified log-rank test.'
  },
  {
    label: 'd.',
    text: 'Compared to a two-sided <i>P</i> value boundary of 0.0109.'
  }
]

const featuredStatFootnotesThree = [
    {
        label: 'e.',
        text: 'HR based on Cox regression model with treatment as a covariate stratified by stage, tumor PD-L1 expression, histology, and geographic region.'
    },
    {
        label: 'f.',
        text: '<i>P</i> value based on stratified log-rank test.'
    },
    {
        label: 'g.',
        text: 'Compared to a two-sided <i>P</i> value boundary of 0.0109.'
    },
    {
      label: 'h.',
      text: 'Median OS based on Kaplan-Meier estimates.'
    }
  ]

const pathologicalRrFootnotes = [
    {
        label: 'i.',
        text: 'HR based on Cox regression model with treatment as a covariate stratified by stage, tumor PD&#8288;-&#8288;L1 expression, histology, and geographic region.'
    },
    {
        label: 'j.',
        text: '<i>P</i> value based on stratified log-rank test.'
    },
    {
        label: 'k.',
        text: 'Compared to a two-sided <i>P</i> value boundary of 0.0092.'
    },
    {
        label: 'l.',
        text: 'Median EFS based on Kaplan-Meier estimates.'
    }
]

const patientProfileJess = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/jess.png" placeholder="TRACED_SVG" alt="..." width={100} height={100} />
    ),
    name: "Eleanor",
    diagnosis: "Stage IIB<sup>s</sup> (T2bN1M0) nonsquamous NSCLC",
    primaryCharacteristics: [
        {
            key: 'Treatment History',
            value: 'No prior history of therapy',
            emphasize: false,
        },
        {
            key: 'Image Exam',
            value: '4.8-cm mass with ipsilateral hilar lymph node involvement',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '0',
            emphasize: false,
        },
        {
            key: 'PD&#8288;-&#8288;L1 Expression ',
            value: 'TPS <1%',
            emphasize: false,
        },     
    ],
    secondaryCharacteristics: [
        {
            key: 'Age:',
            value: '58',
        },
        {
            key: 'Gender:',
            value: 'Female',
        },
        {
            key: 'Physical Activity',
            value: 'Moderate',
        },
    ],
}


const patientProfilePablo = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/pablo.png" placeholder="TRACED_SVG" alt="..." width={100} height={100} />
    ),
    name: "Pablo",
    diagnosis: "Stage IIIA<sup>t</sup> (T1cN2M0) nonsquamous NSCLC",
    primaryCharacteristics: [
        {
            key: 'Treatment History',
            value: 'No prior history of therapy',
            emphasize: false,
        },
        {
            key: 'Imaging Exam',
            value: '2.9-cm mass with ipsilateral hilar and paratracheal lymph node involvement',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '1',
            emphasize: false,
        },
        {
            key: 'PD&#8288;-&#8288;L1 Expression ',
            value: 'TPS = 0%',
            emphasize: false,
        },     
    ],
    secondaryCharacteristics: [
        {
            key: 'Age:',
            value: '73',
        },
        {
            key: 'Gender:',
            value: 'Male',
        },
        {
            key: 'Physical Activity',
            value: 'Low',
        },
    ],
    footnotes: [
        {
            label: 's.',
            text: 'Per AJCC 8th ed (stage IIA per 7th ed).'
        },
        {
            label: 't.',
            text: 'Per AJCC 8th ed (stage IIIA per AJCC 7th ed).'
        }
    ]
}

const pageReferences = [
    {
        label: "1.",
        text: "Spicer JD, Garassino MC, Wakelee H, et al. Neoadjuvant pembrolizumab plus chemotherapy followed by adjuvant pembrolizumab compared with neoadjuvant chemotherapy alone in patients with early-stage non-small-cell lung cancer (KEYNOTE-671): a randomised, double-blind, placebo-controlled, phase 3 trial. <i>Lancet.</i> 2024;404 (10459):1240–1252. doi:10.1016/S0140-6736(24)01756-2"
    },
    {
        label: "2.",
        text: "Data available on request from the Merck National Service Center via email at daprequests@merck.com. Please specify information package US-LAM-04628."
    },
    {
        label: "3.",
        text: "Data available on request from the Merck National Service Center via email at daprequests@merck.com. Please specify information package US-LAM-04630."
    },
    {
        label: "4.",
        text: "Wakelee H, Liberman M, Kato T, et al. Perioperative pembrolizumab for early-stage non-small-cell lung cancer. <i>N Engl J Med.</i> 2023;389(6):491–503. doi:10.1056/NEJMoa2302983"
    },
    {
        label: "5.",
        text: "Data available on request from the Merck National Service Center via email at daprequests@merck.com. Please specify information package US-LAM-04615."
    },
    {
        label: "6.",
        text: "Data available on request from the Merck National Service Center via email at daprequests@merck.com. Please specify information package US-LAM-04629."
    },
    {
        label: "7.",
        text: "Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Non-Small Cell Lung Cancer V.5.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed March 16, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org"
    },
]

const NCCNFootnotes = [
    {
        label: "r.",
        text: "Contraindications for treatment with PD-1/PD-L1 inhibitors may include active or previously documented autoimmune disease and/or current use of immunosuppressive agents; some oncogenic drivers (ie, <i>EGFR</i> exon 19 deletion or exon 21 L858R, <i>ALK</i> , <i>RET</i>, or <i>ROS1</i> rearrangements) have been shown to be associated with less benefit from PD-1/PD-L1 inhibitors.<sup>7</sup>"
    },
]
const NCCNcalloutData = {   
    title: 'NCCN CATEGORY 1 RECOMMENDATION<br/>Pembrolizumab (KEYTRUDA) as Part of a Neoadjuvant Regimen and Then Continued as Adjuvant Treatment After Surgery for Certain Patients With Resectable (T ≥4 cm or N+) NSCLC, Regardless of PD-L1 Expression<sup>7</sup>',
    bodyCopy: `<p>The NCCN Guidelines® recommend as an option pembrolizumab and cisplatin-based doublet chemotherapy as a neoadjuvant treatment, then continued as single-agent pembrolizumab as adjuvant treatment after surgery (category 1).  Platinum-doublet chemotherapy options include<sup>7</sup>:</p>
    <ul>
    <li>Cisplatin/pemetrexed (nonsquamous)</li>
    <li>Cisplatin/gemcitabine (squamous)</li>
    </ul>
    <p>Pembrolizumab and berahyaluronidase alfa-pmph subcutaneous injection may be substituted for IV pembrolizumab. Pembrolizumab and berahyaluronidase alfa-pmph has different dosing and administration instructions compared to IV pembrolizumab.</p>`,
    showBadge: false,
    alt: ""
}

const NCCNdefinitions = `<p>Category 1 = based upon high-level evidence (≥1 randomized phase 3 trials or high-quality, robust meta-analyses), there is uniform NCCN consensus (≥85% support of the Panel) that the intervention is appropriate.<sup>7</sup></p>
                                <p>NCCN makes no warranties of any kind whatsoever regarding their content, use, or application and disclaims any responsibility for their application or use in any way.</p>
                                <p><i>ALK</i> = anaplastic lymphoma kinase; <i>EGFR</i> = epidermal growth factor receptor; <i>RET</i> = rearrangement during transfection; <i>ROS1</i> = ROS proto-oncogene 1, receptor tyrosine kinase.</p>`

const NCCNcalloutReferences = [
    {
    label: "7.",
    text: "Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Non-Small Cell Lung Cancer V.5.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed March 16, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org"    },
]

const anchorLinkModelData = [
    {label: 'Clinical Findings From KEYNOTE-671 for KEYTRUDA'},
    {label: 'Study Design for KEYNOTE&#8288;-&#8288;671 for KEYTRUDA'},
    {label: 'NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)'},
    {label: 'Appropriate Patients'},
];

const clinicalFindingsSubhead = `Statistically Significant Improvement in OS With KEYTRUDA + Chemo/KEYTRUDA vs Placebo + Chemo/Placebo<sup>a</sup>`;

const efsSubhead = `Superior EFS With KEYTRUDA + Chemo/KEYTRUDA vs Placebo + Chemo/Placebo`;


const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={ pageMeta } location={ location }>
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results For KEYTRUDA® (pembrolizumab)" isBranding />
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId}/>
                        <AnchorLinks items={anchorLinkModelData} />
                   </PageSection>
                   <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">
                            <b>KEYTRUDA Is the Only Anti&ndash;PD-1, as Part of a Neoadjuvant Followed by Adjuvant Regimen, to Demonstrate Statistically Significant Improvement in Overall Survival in Resectable</b> (T ≥4 cm or N+) <b>NSCLC</b> vs Placebo + Chemo/Placebo
                        </PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection title={anchorLinkModelData[0].label} bgColor="cloud">
                        <PageSubSection title="Overall Survival (OS) at Second Interim Analysis (Median Follow-Up Time: 36.6 Months)<sup>1</sup>">
                        <StudyTitle as='H3' dangerouslySetInnerHTML={createMarkup(clinicalFindingsSubhead)}></StudyTitle> 
                            <CustomSpacing30>
                        <FeaturedStat
                                statistic="28%"
                                title="Reduction in the risk of death with KEYTRUDA + chemo/KEYTRUDA vs placebo + chemo/placebo<div style='margin-top: 10px;'><b>(HR=0.72; 95% CI, 0.56–0.93; <i>P</i>=0.0103)<sup>b-d</sup></b></div>"
                               
                            />
                            </CustomSpacing30>
                                <CustomSpacing40>
                                    <ComparisonTable
                                    title="OS"
                                        data={
                                            [
                                                [
                                                    {
                                                        legendTitle: 'KEYTRUDA + chemo/KEYTRUDA (n=397)',
                                                    },
                                                    {
                                                        legendTitle: 'Placebo + chemo/placebo<br>(n=400)',
                                                    },
                                                ],
                                                [
                                                    {
                                                        hrCiP: 'HR=0.72; 95% CI, 0.56–0.93; <i>P</i>=0.0103<sup>b-d</sup>',
                                                        colspan: 2,
                                                        centerText: true,
                                                    },
                                                ],
                                                [
                                                    {
                                                        label: 'Number of events',
                                                        dataPoint: '110 (28%)',
                                                    },
                                                    {
                                                        label: 'Number of events',
                                                        dataPoint: '144 (36%)',
                                                    },

                                                ],
                                                [
                                                    {
                                                        label: 'Median OS<sup>e</sup>',
                                                        dataPoint: 'Not reached',
                                                        dataSubPoint: '(95% CI, NR–NR)'
                                                    },
                                                    {
                                                        label: 'Median OS<sup>e</sup>',
                                                        dataPoint: '52.4 months',
                                                        dataSubPoint: '(95% CI, 45.7–NR)'
                                                    },

                                                ],
                                            ]
                                        } />
                                    {/* <WYSIWYG>
                                        <ul>
                                            <li><strong>Events observed:</strong> 28% (110/397) with KEYTRUDA + chemotherapy/KEYTRUDA and 36% (144/400) with placebo + chemotherapy/placebo</li>
                                            <li><strong>Median OS<sup>e</sup></strong>: Not reached (95% CI, NR–NR) with KEYTRUDA + chemotherapy/KEYTRUDA vs 52.4 months (95% CI, 45.7–NR) with placebo + chemotherapy/placebo</li>
                                        </ul>
                                    </WYSIWYG> */}
                                    <NotationBlock
                                        footnotes={[
                                            {
                                                label: 'a.',
                                                text: 'Chemotherapy: Cisplatin 75 mg/m<sup>2</sup> IV on day 1 + (pemetrexed 500 mg/m<sup>2</sup> IV on day 1 or gemcitabine 1,000 mg/m<sup>2</sup> IV on days 1 and 8) of each 21-day cycle for up to 4 cycles.'
                                            },
                                            {
                                                label: 'b.',
                                                text: 'HR based on Cox regression model with treatment as a covariate stratified by stage, tumor PD-L1 expression, histology, and geographic region.'
                                            },
                                            {
                                                label: 'c.',
                                                text: '<i>P</i> value based on stratified log-rank test.'
                                            },
                                            {
                                                label: 'd.',
                                                text: 'Compared to a two-sided <i>P</i> value boundary of 0.0109.'
                                            },
                                            {
                                                label: 'e.',
                                                text: 'Median OS based on Kaplan-Meier estimates.'
                                            },
                                        ]

                                        } />
                                </CustomSpacing40>
                            <CustomSpacing40>
                            <PageSubSection
                            title="Exploratory Analysis of OS at Third Interim Analysis (Median Follow-Up Time: 41.1 Months)<sup>2</sup>"
                            >

                        <StyledTextBox>
                        <TextBlock>
                        <strong>LIMITATION:</strong> This analysis (median follow-up time: 41.1 months) in KEYNOTE-671 was exploratory in nature. No statistical testing was conducted for this analysis, and therefore, no conclusions can be drawn.<sup>2</sup>   
                        </TextBlock>
                        </StyledTextBox>
                        <StyledKMCurve>
                            <KMCurve
                                title="Kaplan-Meier Estimates of OS in KEYNOTE-671"
                                // limitation="<strong>LIMITATION:</strong> This analysis (median follow-up time: 41.1 months) in KEYNOTE-671 was exploratory in nature. No statistical testing was conducted for this analysis, and therefore, no conclusions can be drawn.<sup>2</sup>"
                                hr="HR=0.73 (95% CI, 0.58–0.92)"
                                // additionalInformation={
                                //     [{
                                //         text:`<ul>
                                // <li><strong>Events observed:</strong> 33.0% (131/397) with KEYTRUDA + chemotherapy/KEYTRUDA vs 41.8% (167/400) with placebo + chemotherapy/placebo<sup>2</sup></li>
                                // <li style="margin-bottom: 0px;"><strong>Median OS<sup>f</sup></strong>: Not reached with KEYTRUDA + chemotherapy/KEYTRUDA (95% CI, NR–NR) or with placebo + chemotherapy/placebo (95% CI, 50.3 months–NR)<sup>2</sup></li>
                                // </ul>`
                                // }]
                                // }
                               
                                image={() => <StaticImage src="../../assets/nsclc-km-kn-671-os.png"
                                    placeholder="none"
                                    quality={100}
                                    alt="Kaplan-Meier Estimates of Overall Survival (OS) in KEYNOTE-671" />
                                }
                            />
                            <ComparisonTable
                                    title="OS<sup>2</sup>"
                                        data={
                                            [
                                                [
                                                    {
                                                        legendTitle: 'KEYTRUDA + chemo/KEYTRUDA (n=397)',
                                                    },
                                                    {
                                                        legendTitle: 'Placebo + chemo/placebo<br>(n=400)',
                                                    },
                                                ],
                                                [
                                                    {
                                                        hrCiP: 'HR=0.73; 95% CI, 0.58–0.92',
                                                        colspan: 2,
                                                        centerText: true,
                                                    },
                                                ],
                                                [
                                                    {
                                                        label: 'Number of events',
                                                        dataPoint: '131 (33.0%)',
                                                    },
                                                    {
                                                        label: 'Number of events',
                                                        dataPoint: '167 (41.8%)',
                                                    },

                                                ],
                                                [
                                                    {
                                                        label: 'Median OS<sup>f</sup>',
                                                        dataPoint: 'Not reached',
                                                        dataSubPoint: '(95% CI, NR–NR)'
                                                    },
                                                    {
                                                        label: 'Median OS<sup>f</sup>',
                                                        dataPoint: 'Not reached',
                                                        dataSubPoint: '(95% CI, 50.3 months–NR)'
                                                    },

                                                ],
                                            ]
                                        } />
                            <NotationBlock 
                            footnotes={[                   
                                {
                                    label: 'f.',
                                    text: 'Median OS based on Kaplan-Meier estimates.<sup>2</sup>'
                                },
                            ]}
                            />
                            </StyledKMCurve>
                            </PageSubSection>
                            </CustomSpacing40>
                            <CustomAccordianSpacing>
                            <AccordionGroup items={[
                                {
                                    title:"Exploratory Subgroup Analysis of OS in KEYNOTE-671<sup>3</sup>",
                                    children:<ForestPlot
                                    title="Exploratory Subgroup Analysis of OS<sup>g</sup> at Third Interim Analysis (Median Follow-up Time: 41.1 Months)<sup>3</sup>"
                                    limitation="<strong>LIMITATION:</strong> KEYNOTE-671 was not powered to detect differences in the treatment effect in these subgroups; therefore, no conclusions can be drawn.<sup>3</sup><p>Additionally, the subgroups for ECOG 0, ECOG 1, PD-L1 TPS 1%-49%, PD-L1 TPS ≥1%, PD-L1 TPS <1%, <i>EGFR</i> Activating Mutation Status Unknown/Missing, and <i>ALK</i> Translocation Status Unknown/Missing were not prespecified in the Statistical Analysis Plan.<sup>3</sup></p>"
                                    definitions="<p><sup>g</sup>Per the prespecified analysis plan, subgroups with <30 patients were excluded from the forest plot. Data cutoff date: August 19, 2024.<sup>3</sup></p><p><i>ALK</i> = anaplastic lymphoma kinase; CI = confidence interval; ECOG = Eastern Cooperative Oncology Group; <i>EGFR</i> = epidermal growth factor receptor; TPS = tumor proportion score.</p>"
                                    image = {() =>
                                        <StaticImage src="../../assets/nsclc-forest-kn-671-os.png"
                                    placeholder="none"
                                    alt="Exploratory Subgroup Analysis of OS in KEYNOTE-671" />
                                    }
                                    />
                                }
                            ]}  homepage />
                            </CustomAccordianSpacing>
                            {/* <DefinitionBlock
                            text="<p>*Per the prespecified analysis plan, subgroups with <30 patients were excluded from the forest plot. Data cutoff date: August 19, 2024.<sup>3</sup></p><p><i>ALK</i> = anaplastic lymphoma kinase; ECOG = Eastern Cooperative Oncology Group; <i>EGFR</i> = epidermal grow factor receptor; TPS = tumor proportion score.</p>" /> */}
                            
                            
                        </PageSubSection>
                        <PageSubSection title="Event-Free Survival (EFS)<sup>h</sup> at First Interim Analysis (Median Follow-up Time: 25.2 Months)<sup>4</sup>">
                        <StudyTitle as='H3' dangerouslySetInnerHTML={createMarkup(efsSubhead)}></StudyTitle> 
                        
                            {/* <SecondaryCallout
                                title='Statistically significant improvement in EFS with KEYTRUDA + chemotherapy/KEYTRUDA vs placebo + chemotherapy/placebo<sup>f</sup>'
                                alt=""
                            /> */}
                            <CustomSpacing30>
                            <FeaturedStat
                                statistic="42%"
                                title="Reduction in the risk of event (progression, recurrence, or death) with KEYTRUDA + chemo/KEYTRUDA vs placebo + chemo/placebo<div style='margin-top: 10px;'><b>(HR=0.58; 95% CI, 0.46&ndash;0.72; <i>P</i><0.0001)<sup>i-k</sup></b></div>"
                            />
                            </CustomSpacing30>
                            <ComparisonTable
                                    title="EFS"
                                        data={
                                            [
                                                [
                                                    {
                                                        legendTitle: 'KEYTRUDA + chemo/KEYTRUDA (n=397)',
                                                    },
                                                    {
                                                        legendTitle: 'Placebo + chemo/placebo<br>(n=400)',
                                                    },
                                                ],
                                                [
                                                    {
                                                        hrCiP: 'HR=0.58; 95% CI, 0.46–0.72; <i>P</i><0.0001<sup>i-k</sup>',
                                                        colspan: 2,
                                                        centerText: true,
                                                    },
                                                ],
                                                [
                                                    {
                                                        label: 'Number of events',
                                                        dataPoint: '139 (35%)',
                                                    },
                                                    {
                                                        label: 'Number of events',
                                                        dataPoint: '205 (51%)',
                                                    },

                                                ],
                                                [
                                                    {
                                                        label: 'Median EFS<sup>l</sup>',
                                                        dataPoint: 'Not reached',
                                                        dataSubPoint: '(95% CI, 34.1 months–NR)'
                                                    },
                                                    {
                                                        label: 'Median EFS<sup>l</sup>',
                                                        dataPoint: '17.0 months',
                                                        dataSubPoint: '(95% CI, 14.3–22.0)'
                                                    },

                                                ],
                                            ]
                                        } />
                            {/* <WYSIWYG>
                            <ul>
                                <li><strong>Events observed:</strong> 35% (139/397) with KEYTRUDA + chemotherapy/KEYTRUDA and 51% (205/400) with placebo + chemotherapy/placebo</li>
                                <li><strong>Median EFS<sup>j</sup></strong>: Not reached (95% CI, 34.1–NR) with KEYTRUDA + chemotherapy/KEYTRUDA vs 17.0 months (95% CI, 14.3–22.0) with placebo + chemotherapy/placebo</li>
                                </ul>
                            </WYSIWYG> */}
                            <NotationBlock 
                             footnotes={[
                                {
                                    label: 'h.',
                                    text: 'EFS was defined as the time from randomization to the first occurrence of local progression that precluded the planned surgery, unresectable tumor, progression or recurrence according to RECIST v1.1, by the investigator’s assessment, or death from any cause.<sup>4</sup>'
                                },
                                {
                                    label: 'i.',
                                    text: 'HR based on Cox regression model with treatment as a covariate stratified by stage, tumor PD-L1 expression, histology, and geographic region.'
                                },
                                {
                                    label: 'j.',
                                    text: '<i>P</i> value based on stratified log-rank test.'
                                },
                                {
                                    label: 'k.',
                                    text: 'Compared to a two-sided <i>P</i> value boundary of 0.0092.'
                                },
                                {
                                    label: 'l.',
                                    text: 'Median EFS based on Kaplan-Meier estimates.'
                                },
                            ]

                            }
                            definitions="RECIST v1.1 = Response Evaluation Criteria In Solid Tumors v1.1."
                            />
                            
                        </PageSubSection>
                        <PageSubSection title="Pathological Response Rates at First Interim Analysis (Median Follow-Up Time: 25.2 Months)<sup>4</sup>"> 
                                <TextBlock>
                                    {htmlPRR}
                                </TextBlock>
                                <WYSIWYG>
                            <ul>
                                <li><strong>pCR rate:</strong> 18.1% with KEYTRUDA + chemotherapy vs 4.0% with placebo + chemotherapy (<i>P</i>&lt;0.0001)</li>
                                <li><strong>mPR rate:</strong> 30.2% with KEYTRUDA + chemotherapy vs 11.0% with placebo + chemotherapy (<i>P</i>&lt;0.0001)</li>
                                </ul>
                            </WYSIWYG>
                            </PageSubSection>
                        <PageSubSection title="Exploratory Analysis of EFS at Third Interim Analysis (Median Follow-Up Time: 41.1 Months)<sup>5</sup>">
                        <StyledTextBox>
                        <TextBlock>
                        <strong>LIMITATION:</strong> This analysis (median follow-up time: 41.1 months) in KEYNOTE-671 was exploratory in nature. No statistical testing was conducted for this analysis, and therefore, no conclusions can be drawn.<sup>5</sup>
                        </TextBlock>
                        </StyledTextBox>
                        <StyledKMCurve>
                        <KMCurve
                                title="Kaplan-Meier Estimates of EFS in KEYNOTE-671"
                                // limitation={`<strong>LIMITATION:</strong> This analysis (median follow-up time: 41.1 months) in KEYNOTE-671 was exploratory in nature. No statistical testing was conducted for this analysis, and therefore, no conclusions can be drawn.<sup>5</sup>
                                //     <h4>Kaplan-Meier Estimates of EFS in KEYNOTE-671</h4>`}
                                hr="HR=0.57 (95% CI, 0.47–0.69)"
                                // additionalInformation={
                                //     [{
                                //         text:`<ul>
                                // <li><strong>Events observed:</strong> 46.6% (185/397) with KEYTRUDA + chemotherapy/KEYTRUDA vs 67.3% (269/400) with placebo + chemotherapy/placebo<sup>5</sup></li>
                                // <li style="margin-bottom: 0px;"><strong>Median EFS<sup>l</sup></strong>: 57.1 months (95% CI, 38.0&ndash;69.1) with KEYTRUDA + chemotherapy/KEYTRUDA and 18.4 months (95% CI, 14.8&ndash;22.1) with placebo + chemotherapy/placebo<sup>5</sup></li>
                                // </ul>`
                                // }]
                                // }
                               
                                image={() => <StaticImage src="../../assets/nsclc-km-kn-671-efs.png"
                                    placeholder="none"
                                    alt="Kaplan-Meier Estimates of Event-Free Survival in KEYNOTE-671" />
                                }
                            />
                             <ComparisonTable
                                    title="EFS<sup>5</sup>"
                                        data={
                                            [
                                                [
                                                    {
                                                        legendTitle: 'KEYTRUDA + chemo/KEYTRUDA (n=397)',
                                                    },
                                                    {
                                                        legendTitle: 'Placebo + chemo/placebo<br>(n=400)',
                                                    },
                                                ],
                                                [
                                                    {
                                                        hrCiP: 'HR=0.57; 95% CI, 0.47–0.69',
                                                        colspan: 2,
                                                        centerText: true,
                                                    },
                                                ],
                                                [
                                                    {
                                                        label: 'Number of events',
                                                        dataPoint: '185 (46.6%)',
                                                    },
                                                    {
                                                        label: 'Number of events',
                                                        dataPoint: '269 (67.3%)',
                                                    },

                                                ],
                                                [
                                                    {
                                                        label: 'Median EFS<sup>m</sup>',
                                                        dataPoint: '57.1 months',
                                                        dataSubPoint: '(95% CI, 38.0–69.1)'
                                                    },
                                                    {
                                                        label: 'Median EFS<sup>m</sup>',
                                                        dataPoint: '18.4 months',
                                                        dataSubPoint: '(95% CI, 14.8–22.1)'
                                                    },

                                                ],
                                            ]
                                        } />
                             <NotationBlock 
                             footnotes={[
                                {
                                    label: 'm.',
                                    text: 'Median EFS based on Kaplan-Meier estimates.<sup>5</sup>'
                                },]} />
                            </StyledKMCurve>
                            <CustomAccordianSpacing>
                             <AccordionGroup items={[
                                {
                                    title:"Exploratory Subgroup Analysis of EFS in KEYNOTE-671<sup>6</sup>",
                                    children:<ForestPlot
                                    title="Exploratory Subgroup Analysis of EFS<sup>n</sup> at Third Interim Analysis (Median Follow-Up Time: 41.1 Months)<sup>6</sup>"
                                    limitation="<strong>LIMITATION:</strong> KEYNOTE-671 was not powered to detect differences in the treatment effect in these subgroups; therefore, no conclusions can be drawn.<sup>6</sup><p>Additionally, the subgroups for ECOG 0, ECOG 1, PD-L1 TPS 1%-49%, PD-L1 TPS ≥1%, PD-L1 TPS <1%, <i>EGFR</i> Activating Mutation Status Unknown/Missing, and <i>ALK</i> Translocation Status Unknown/Missing were not prespecified in the Statistical Analysis Plan.<sup>6</sup></p>"
                                    definitions="<p><sup>n</sup>Per the prespecified analysis plan, subgroups with <30 patients were excluded from the forest plot. Data cutoff date: August 19, 2024.<sup>6</sup></p>"
                                    image = {() =>
                                        <StaticImage src="../../assets/nsclc-forest-kn-671-efs.png"
                                    placeholder="none"
                                    alt="Exploratory Subgroup Analysis of EFS in KEYNOTE-671" />
                                    }
                                    />
                                }
                            ]}  homepage />
                           
                            </CustomAccordianSpacing>
                            {/* <DefinitionBlock
                            text="<p>*Per the prespecified analysis plan, subgroups with <30 patients were excluded from the forest plot. Data cutoff date: August 19, 2024.<sup>6</sup></p><p><i>ALK</i> = anaplastic lymphoma kinase; ECOG = Eastern Cooperative Oncology Group; <i>EGFR</i> = epidermal grow factor receptor; TPS = tumor proportion score.<p>" /> */}
                        </PageSubSection>
                        {/* <SecondaryCallout 
                        title="Pathological Response Rates"
                        text={htmlPRR}
                        /> */}
                            
                        </PageSection>

                            <CustomPageSection>
                        <PageSection bgColor='white'>
                            <PageSubSection title={anchorLinkModelData[1].label}>
                                <NSCLC_NEOADJUVANT />
                            </PageSubSection>
                        </PageSection>
                        </CustomPageSection>

                        <PageStyle>
                        <PageSection bgColor="cloud" id="nccn-clinical-practice-guidelines-in-oncology-nccn-guidelines" title={anchorLinkModelData[2].label}>
                        <CustomNCCNCalloutWrapper>
                        {/* <Heading.H3 as='H2' dangerouslySetInnerHTML={createMarkup(anchorLinkModelData[2].label)}></Heading.H3>  */}
                                <NCCNcallout
                                        data={NCCNcalloutData}
                                        // footnotes={NCCNFootnotes}
                                        definitions={NCCNdefinitions}
                                        references={NCCNcalloutReferences}
                                    />
                                    </CustomNCCNCalloutWrapper>
                        </PageSection>
                        </PageStyle>

                        <PageSection
                            bgColor="white"
                            title={anchorLinkModelData[3].label}
                        >
                            <PageSubSection
                                title='Hypothetical Patients Based on Those Studied in KEYNOTE-671'
                            >
                                <PatientProfile {...patientProfileJess} />
                                <PatientProfile {...patientProfilePablo} />
                            

                                <SecondaryCallout 
                                    title="For your eligible patients with resectable (tumors ≥4 cm or node positive) NSCLC, start with KEYTRUDA in combination with platinum-containing chemotherapy in the neoadjuvant setting, then stay with KEYTRUDA as a single agent in the adjuvant setting after surgery."
                                    alt=""
                                />
                            </PageSubSection>
                        </PageSection>
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>
                
                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;